// ----------------------------------------------------------------------------
// createBucketManagerUI — modal panel for listing / searching / deleting
// named buckets. Works alongside BucketManager and BucketService.
//
// Usage:
//   const mgr = createBucketManagerUI({ BucketManager, bucket, onSwitch, textInputModal });
//   document.getElementById('bucket-tab-manage').addEventListener('click', () => mgr.open());
// ----------------------------------------------------------------------------

export function createBucketManagerUI({ BucketManager, bucket, onSwitch, textInputModal }) {
   const modal   = document.getElementById('bucket-manager-modal');
   const listEl  = document.getElementById('bkm-list');
   const searchEl = document.getElementById('bkm-search');
   const closeBtn = document.getElementById('bkm-close');
   const newBtn   = document.getElementById('bkm-new');
   if (!modal || !listEl) return { open() {}, close() {} };

   // pending-delete state: id → true (showing confirm row)
   const pendingDelete = new Set();

   // ---- item counts per bucket (async) ------------------------------------
   async function getItemCounts(buckets) {
      const counts = {};
      await Promise.all(buckets.map(async (b) => {
         try {
            const { dbGetAll } = await import('./bucket.js');
            const items = await dbGetAll(b.id);
            counts[b.id] = items.length;
         } catch (_) { counts[b.id] = 0; }
      }));
      return counts;
   }

   // ---- render ------------------------------------------------------------
   async function render() {
      listEl.innerHTML = '<li class="bkm-loading">loading…</li>';
      const buckets = await BucketManager.list();
      const counts  = await getItemCounts(buckets);
      const q = (searchEl && searchEl.value || '').toLowerCase();
      const visible = buckets.filter((b) => b.name.toLowerCase().includes(q));

      listEl.innerHTML = '';

      if (!visible.length) {
         listEl.innerHTML = '<li class="bkm-empty">no buckets match</li>';
         return;
      }

      for (const bkt of visible) {
         const li = document.createElement('li');
         li.className = 'bkm-item' + (bkt.id === bucket.bucketId ? ' bkm-item-active' : '');
         if (bkt.isOpen === false) li.classList.add('bkm-item-closed');
         li.dataset.id = bkt.id;

         // main row
         const row = document.createElement('div');
         row.className = 'bkm-item-row';

         const nameEl = document.createElement('span');
         nameEl.className = 'bkm-item-name';
         nameEl.textContent = bkt.name;
         nameEl.title = 'Double-click to rename';
         nameEl.addEventListener('dblclick', async () => {
            const newName = typeof textInputModal === 'function'
               ? await textInputModal({ title: 'Rename bucket', placeholder: 'Enter name…', defaultValue: bkt.name })
               : prompt('Rename bucket:', bkt.name);
            if (!newName || newName === bkt.name) return;
            await BucketManager.rename(bkt.id, newName);
            render();
            if (typeof onSwitch === 'function') onSwitch();
         });

         const countEl = document.createElement('span');
         countEl.className = 'bkm-item-count';
         countEl.textContent = counts[bkt.id] + ' sample' + (counts[bkt.id] === 1 ? '' : 's');

         const actions = document.createElement('div');
         actions.className = 'bkm-item-actions';

         // Show open button if closed, switch button if open
         if (bkt.isOpen === false) {
            const openBtn = document.createElement('button');
            openBtn.className = 'bkm-btn-switch';
            openBtn.textContent = 'open';
            openBtn.addEventListener('click', async () => {
               await BucketManager.open(bkt.id);
               await bucket.switchBucket(bkt.id);
               if (typeof onSwitch === 'function') onSwitch();
               render();
            });
            actions.appendChild(openBtn);
         } else {
            const switchBtn = document.createElement('button');
            switchBtn.className = 'bkm-btn-switch';
            switchBtn.textContent = bkt.id === bucket.bucketId ? 'active' : 'open';
            switchBtn.disabled = bkt.id === bucket.bucketId;
            switchBtn.addEventListener('click', async () => {
               await bucket.switchBucket(bkt.id);
               if (typeof onSwitch === 'function') onSwitch();
               render();
            });
            actions.appendChild(switchBtn);
         }

         if (bkt.id !== 'default') {
            const delBtn = document.createElement('button');
            delBtn.className = 'bkm-btn-del icons2';
            delBtn.innerHTML = '\uE848';
            delBtn.title = 'Delete bucket';
            delBtn.addEventListener('click', () => {
               pendingDelete.add(bkt.id);
               renderConfirm(li, bkt, counts[bkt.id]);
            });
            actions.appendChild(delBtn);
         }

         row.appendChild(nameEl);
         row.appendChild(countEl);
         row.appendChild(actions);
         li.appendChild(row);

         if (pendingDelete.has(bkt.id)) {
            renderConfirm(li, bkt, counts[bkt.id]);
         }

         listEl.appendChild(li);
      }
   }

   function renderConfirm(li, bkt, count) {
      // remove any existing confirm
      const existing = li.querySelector('.bkm-confirm');
      if (existing) existing.remove();

      const conf = document.createElement('div');
      conf.className = 'bkm-confirm';
      conf.innerHTML = `<span>Delete <b>${bkt.name}</b> and ${count} sample(s)?</span>`;

      const yes = document.createElement('button');
      yes.className = 'bkm-confirm-yes';
      yes.textContent = 'delete';
      yes.addEventListener('click', async () => {
         // if deleting active bucket, switch to default first
         if (bkt.id === bucket.bucketId) {
            await bucket.switchBucket('default');
         }
         await BucketManager.remove(bkt.id);
         pendingDelete.delete(bkt.id);
         if (typeof onSwitch === 'function') onSwitch();
         render();
      });

      const no = document.createElement('button');
      no.className = 'bkm-confirm-no';
      no.textContent = 'cancel';
      no.addEventListener('click', () => {
         pendingDelete.delete(bkt.id);
         render();
      });

      conf.appendChild(yes);
      conf.appendChild(no);
      li.appendChild(conf);
   }

   // ---- open / close ------------------------------------------------------
   function open() {
      pendingDelete.clear();
      modal.removeAttribute('hidden');
      if (searchEl) { searchEl.value = ''; searchEl.focus(); }
      render();
   }

   function close() {
      modal.setAttribute('hidden', '');
      pendingDelete.clear();
   }

   // ---- events ------------------------------------------------------------
   closeBtn && closeBtn.addEventListener('click', close);
   modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
   searchEl && searchEl.addEventListener('input', render);

   newBtn && newBtn.addEventListener('click', async () => {
      const name = typeof textInputModal === 'function'
         ? await textInputModal({ title: 'New bucket name', placeholder: 'Enter name…', defaultValue: 'Bucket ' + (Date.now() % 1000) })
         : prompt('New bucket name:', 'Bucket ' + (Date.now() % 1000));
      if (!name) return;
      const bkt = await BucketManager.create(name);
      await bucket.switchBucket(bkt.id);
      if (typeof onSwitch === 'function') onSwitch();
      render();
   });

   document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) close();
   });

   return { open, close, refresh: render };
}
